import { Plus, ArrowDown, ArrowUp, X } from "lucide-react";
import { useEffect, useRef } from "react";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import { PosterCard, PosterCardHeader } from "@/shared/ui/PosterCard";
import { cn } from "@/shared/lib/utils";
import {
  hasCover,
  isCoverAt,
  movePoint,
  normalizeRoles,
  setCoverEnabled,
} from "../../../model/free-create/groupScript";
import { submittableGroupPoints } from "../../../model/free-create/outputMode";
import type { GroupPointForm } from "../../../model/blueprint";

export const MAX_GROUP_POINTS = 6;

/**
 * 组图台本卡 —— B1(2026-08-03 设计稿),接替上一版「蓝底辅助面板 + 下方 hero 大框」。
 *
 * ═══ 为什么整合成一张卡 ═══════════════════════════════════════════════════
 * 上一版的组图态是两个框:台本面板(分)在上、「想要一张什么样的图?」hero 大框(总)
 * 在下 —— 职责不清(大框其实是整组共同说明,但标题是单图文案)、总分倒挂、视觉权重
 * 倒挂(最重的框装配角内容)。B1 收成**一张 hero 台本卡**,自上而下 总 → 分:
 *   ① 这组图卖什么:产品入口(productSlot)+ 整组共同说明(绑 form.prompt,payload 零改)
 *   ② 逐张台本:每张一卡(封面标/大字/画面/排序/删)
 * 组图态下 PromptPanel 不再渲染,⚙参数 与 品牌入口 两个槽挪进本卡底部。
 *
 * ═══ 「+ 添加一张」直接追加空卡(草稿行退役)═══════════════════════════════
 * 上一版末尾是「输入爆点 + 添加按钮」,由此生出「打了字没点添加」这个要靠一行黄字
 * 解释的状态(W4 收编补丁)。现在点添加直接得到一张空卡、大字框自动聚焦 ——
 * 那个状态从根上不存在了。新口径:**没写大字的卡不算张数、不出图**
 * (submittableGroupPoints 单一真相;卡面就地提示,≥1 张有字才能生成)。
 *
 * ═══ 保留的两件事(别在下次重构里弄丢)═════════════════════════════════════
 * ① 「将出 N 张」与底栏张数、积分预估同一个真相(submittableGroupPoints);
 * ② 产品卖点一键填充:不覆盖已填的、重复的跳过、满了就不给入口。
 *
 * 封面机制不变:身份只由位置决定,排序后新的第 1 条自动接手(groupScript.ts 头注)。
 */
export function GroupScriptPanel({
  points,
  onPointsChange,
  disabled,
  /** 产品卖点一键填充(选了产品才给)。 */
  fillFrom,
  /** ① 区:产品入口(FreeCreateBody 传 ProductPickerDrawer 及实拍锚行)。 */
  productSlot,
  /** ① 区:整组共同说明(= form.prompt,提交契约不变,只是换了容器)。 */
  brief,
  onBriefChange,
  /** 卡底:⚙ 参数(比例/清晰度/引擎)。原挂在 PromptPanel,组图态下随它一起搬进来。 */
  paramsSlot,
  /** 卡底:品牌/特别要求入口条(同上)。 */
  entriesSlot,
  /** ① 区:「AI 排台本」按钮(FreeCreateBody 持逻辑:确认/请求/落卡)。 */
  draftSlot,
  /** ① 区:「整组风格」chip(2026-08-03 C:画风/配色/字体三条带收敛进来)。 */
  styleSlot,
  /** 卡底:一致性摘要行(产品/版式/风格三 chip)。 */
  consistencySlot,
  /** coverage:某张大字的出处(AI 排台本的 source);无则不渲染 ⓘ。 */
  sourceOf,
  className,
}: {
  points: GroupPointForm[];
  onPointsChange: (points: GroupPointForm[]) => void;
  disabled?: boolean;
  fillFrom?: { productName: string; sellingPoints: string[] } | null;
  productSlot?: React.ReactNode;
  brief: string;
  onBriefChange: (v: string) => void;
  paramsSlot?: React.ReactNode;
  entriesSlot?: React.ReactNode;
  draftSlot?: React.ReactNode;
  styleSlot?: React.ReactNode;
  consistencySlot?: React.ReactNode;
  sourceOf?: (title: string) => string | undefined;
  className?: string;
}) {
  const submittable = submittableGroupPoints(points) ?? [];
  const blankCount = points.length - submittable.length;
  const full = points.length >= MAX_GROUP_POINTS;
  const coverOn = hasCover(points);

  /** 「+ 添加一张」追加空卡后,把焦点送进新卡的大字框(否则用户还得自己找)。 */
  const focusIndexRef = useRef<number | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (focusIndexRef.current == null) return;
    const idx = focusIndexRef.current;
    focusIndexRef.current = null;
    listRef.current
      ?.querySelector<HTMLInputElement>(`[data-testid="group-point-${idx}"] input`)
      ?.focus();
  }, [points.length]);

  const addCard = () => {
    if (full) return;
    focusIndexRef.current = points.length;
    onPointsChange(normalizeRoles([...points, { title: "" }]));
  };

  // ★B-02:onChange 不许再 .slice() 归一 —— 上限交给输入框的 maxLength。
  //   受控 value 被二次归一后与 DOM 短暂不一致,顶到上限在中间插字时 React 回写
  //   会把光标拽到末尾(「改提示词时光标定位不到」的病灶)。
  const patch = (i: number, next: Partial<GroupPointForm>) => {
    onPointsChange(points.map((p, j) => (j === i ? { ...p, ...next } : p)));
  };

  const remove = (i: number) => {
    onPointsChange(normalizeRoles(points.filter((_, j) => j !== i)));
  };

  /** 把产品库里逐字的卖点填进台本 —— 不覆盖已填的,只补到上限。 */
  const fillFromProduct = () => {
    if (!fillFrom) return;
    const have = new Set(points.map((p) => p.title));
    const next = [...points];
    for (const sp of fillFrom.sellingPoints) {
      const title = sp.trim().slice(0, 30);
      if (!title || have.has(title) || next.length >= MAX_GROUP_POINTS) continue;
      next.push({ title });
      have.add(title);
    }
    onPointsChange(normalizeRoles(next));
  };

  const fillable =
    fillFrom &&
    !full &&
    fillFrom.sellingPoints.some((sp) => !points.some((p) => p.title === sp.trim().slice(0, 30)));

  return (
    // ★组图态的页面主体就是这张卡(hero 规格与 PromptPanel 同款):组图态下 PromptPanel
    //   不渲染,一屏内仍只有一个最重的框。
    <PosterCard
      className={cn("border-primary/50 shadow-[0_4px_18px_rgba(37,99,235,0.10)]", className)}
      data-testid="group-script-card"
    >
      <PosterCardHeader>
        <h2 className="text-[19px] font-extrabold leading-tight tracking-[-0.2px]">组图台本</h2>
        <p className="mt-0.5 text-[12.5px] text-muted-foreground">
          每张讲一个爆点,不是同一张的变体 · 小红书轮播 / 详情组图 · 最多 {MAX_GROUP_POINTS} 张
        </p>
        {/* BPWB-2(2026-09-03)「不写不出」状态汇总 —— 提交前就知道会出几张,不用等提交
            后才发现 6 张变 2 张。★「也不收钱」不是随口一句:组图按台本条数分张,提交侧
            submittableGroupPoints 滤过的空卡根本不会写进 groupPoints 数组上送(见
            posterService.ts CreateReq.groupPoints 注释「requestedCount 被忽略,张数=
            爆点数」)—— 空卡连请求体都进不去,自然不计费。 */}
        {blankCount > 0 && (
          <p data-testid="group-blank-summary" className="mt-1 text-[12.5px] text-warning">
            {points.length} 张里还有 {blankCount} 张空着 · 空卡不会出图,也不收钱
          </p>
        )}
      </PosterCardHeader>

      <div className="flex flex-col gap-3 px-5 pb-5">
        {/* ── ① 这组图卖什么(总)────────────────────────────────────── */}
        <div className="rounded-xl border border-border/60 bg-background/40 p-3">
          {productSlot}
          {styleSlot && <div className="mt-2">{styleSlot}</div>}
          <Textarea
            value={brief}
            onChange={(e) => onBriefChange(e.target.value)}
            disabled={disabled}
            rows={2}
            maxLength={2000}
            aria-label="整组共同说明"
            placeholder="整组共同说明:场景基调 / 风格 / 人群(例:轻奢厨房,金色点缀,送礼场景)"
            className="mt-2 min-h-[56px] resize-none text-[13.5px] leading-relaxed"
          />
          <p className="mt-1 text-[12px] text-muted-foreground">
            写一次,整组每张都算数;每张各自的内容写在下面。
          </p>
          {draftSlot && <div className="mt-2">{draftSlot}</div>}
        </div>

        {/* ── ② 逐张台本(分)──────────────────────────────────────── */}
        <ul ref={listRef} className="flex flex-col gap-2">
          {points.map((p, i) => {
            const cover = isCoverAt(points, i);
            const blank = p.title.trim().length === 0;
            return (
              <li
                key={i}
                className={cn(
                  "rounded-md border bg-card px-2.5 py-2",
                  blank ? "border-warning/50" : "border-border/70"
                )}
                data-testid={`group-point-${i}`}
              >
                <div className="flex items-center gap-2">
                  <span className="w-4 shrink-0 text-center text-[12.5px] font-medium text-muted-foreground">
                    {i + 1}
                  </span>

                  {/* 封面标只在第 1 条可切 —— 封面就是第一张,别处出现"封面"会说不清 */}
                  {i === 0 ? (
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => onPointsChange(setCoverEnabled(points, !coverOn))}
                      aria-pressed={cover}
                      title={
                        cover
                          ? "这一张按封面出:画面信息少、留白足,负责让人点进来。点一下改成普通内页"
                          : "这一组不出封面,全部按内页出。点一下让第 1 张当封面"
                      }
                      className={cn(
                        "shrink-0 rounded-full border px-2 py-0.5 text-2xs font-medium transition-colors",
                        cover
                          ? "border-primary/60 bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:border-primary/40"
                      )}
                    >
                      {cover ? "封面" : "内页"}
                    </button>
                  ) : (
                    <span className="shrink-0 rounded-full border border-transparent px-2 py-0.5 text-2xs text-muted-foreground">
                      内页
                    </span>
                  )}

                  <Input
                    value={p.title}
                    disabled={disabled}
                    onChange={(e) => patch(i, { title: e.target.value })}
                    aria-label={`第 ${i + 1} 张的大字`}
                    /* 蓝图带来的参考句只当 placeholder:用户一眼看见这一位该说什么,
                       但写下去的必须是自家的话(2026-08-13 统一口径,与长图区块名同规)。 */
                    placeholder={p.hint ? `参考:${p.hint} —— 写你自己的` : "这张图的大字"}
                    className="h-7 flex-1 text-[13px]"
                    maxLength={30}
                  />
                  {sourceOf?.(p.title) && (
                    <span
                      className="shrink-0 cursor-help text-2xs text-muted-foreground"
                      title={`改写自:${sourceOf(p.title)}`}
                      aria-label={`第 ${i + 1} 张大字的出处`}
                    >
                      ⓘ
                    </span>
                  )}

                  {/* BPWB-2(2026-09-03)「不写不出」状态常驻可见 —— 判据与底栏「将出 N 张」
                      同一个 submittableGroupPoints(单源,不在 UI 层另写一份空标题判断)。 */}
                  <span
                    data-testid={`group-point-status-${i}`}
                    title={blank ? "写上你的话这张才会出" : undefined}
                    className={cn(
                      "shrink-0 rounded-full border px-2 py-0.5 text-2xs font-medium",
                      blank ? "border-warning/50 text-warning" : "border-success/50 text-success"
                    )}
                  >
                    {blank ? "暂不出" : "会出这张"}
                  </span>

                  <button
                    type="button"
                    aria-label={`第 ${i + 1} 张上移`}
                    disabled={disabled || i === 0}
                    onClick={() => onPointsChange(movePoint(points, i, i - 1))}
                    className="shrink-0 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label={`第 ${i + 1} 张下移`}
                    disabled={disabled || i === points.length - 1}
                    onClick={() => onPointsChange(movePoint(points, i, i + 1))}
                    className="shrink-0 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label={`删除第 ${i + 1} 张`}
                    disabled={disabled}
                    onClick={() => remove(i)}
                    className="shrink-0 text-muted-foreground transition-colors hover:text-destructive"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* 画面提示 —— 组图独有的那半句「这张画什么」。可空,空了引擎自己发挥。 */}
                <div className="mt-1.5 flex items-center gap-2 pl-6">
                  <span className="shrink-0 text-[12px] text-muted-foreground">画面</span>
                  <Input
                    value={p.detail ?? ""}
                    disabled={disabled}
                    onChange={(e) => patch(i, { detail: e.target.value })}
                    aria-label={`第 ${i + 1} 张的画面提示`}
                    placeholder={
                      cover
                        ? "封面画什么(可不填)—— 例:厨房台面,产品居中,上方留白"
                        : "这张画什么(可不填)"
                    }
                    className="h-7 flex-1 text-[12.5px]"
                    maxLength={120}
                  />
                </div>

                {blank && (
                  <p className="mt-1 pl-6 text-[12px] text-warning">还没写大字,生成时跳过。</p>
                )}
              </li>
            );
          })}
        </ul>

        <div className="flex flex-wrap items-center gap-2">
          {!full && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 gap-1 px-2.5 text-[13px]"
              disabled={disabled}
              onClick={addCard}
            >
              <Plus className="h-3.5 w-3.5" />
              添加一张
            </Button>
          )}
          {fillable && (
            <button
              type="button"
              disabled={disabled}
              onClick={fillFromProduct}
              className="text-[12.5px] font-medium text-primary underline-offset-2 hover:underline"
            >
              把「{fillFrom!.productName}」的 {fillFrom!.sellingPoints.length} 条卖点填进台本 →
            </button>
          )}
        </div>

        <p className="text-[12.5px] text-muted-foreground">
          将出 <span className="font-medium text-foreground">{submittable.length}</span> 张 · 最多{" "}
          {MAX_GROUP_POINTS}
          {coverOn && submittable.length > 0 && <span className="ml-1">(第 1 张按封面出)</span>}
          {full && <span className="ml-1 text-warning">已达上限</span>}
          {blankCount > 0 && (
            <span className="ml-1.5 text-warning">{blankCount} 张还没写大字,不算在内</span>
          )}
        </p>
        {submittable.length === 0 && (
          <p className="-mt-2 text-[12.5px] text-destructive">至少写 1 张的大字才能生成组图。</p>
        )}

        {/* ── 卡底:⚙参数 + 品牌/特别要求(原 PromptPanel 的两个槽)──────── */}
        {(paramsSlot || entriesSlot || consistencySlot) && (
          <div className="flex flex-col gap-1.5 border-t border-border/50 pt-2.5">
            {consistencySlot}
            {entriesSlot}
            {paramsSlot && <div className="flex items-center gap-1.5">{paramsSlot}</div>}
          </div>
        )}
      </div>
    </PosterCard>
  );
}
